import { useEffect, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { Smartphone, X } from "lucide-react";
import { BrandMark } from "./ui/brand-mark";
import {
  buildDeepLink,
  isDismissed,
  isPhoneWeb,
  rememberDismissed,
} from "../lib/openInApp";

/**
 * Dismissible "Open in the app" banner for mobile-web visitors.
 *
 * Shown only on phone-web (touch-primary pointer + narrow viewport over
 * http(s); see `isPhoneWeb`), and only until dismissed once — dismissal is
 * remembered in localStorage so it isn't naggy. Tapping "Open" launches the
 * `tailspend://` deep link for the current route; Expo Router maps it onto the
 * same screen the web path shows. If the app isn't installed the deep link is
 * a no-op and the web experience continues uninterrupted — we deliberately
 * don't force a store redirect.
 *
 * Rendered once at the app root (see App.tsx) so it appears above every route,
 * including the public share links (`/groups/:id/join`) that are the whole
 * point of the affordance.
 */
export function OpenInAppBanner(): ReactNode {
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  // Decide visibility on mount (and when the pointer/viewport changes, e.g. a
  // 2-in-1 folding into tablet mode). Kept in an effect so SSR/first paint
  // never assumes a DOM.
  useEffect(() => {
    const update = () => setVisible(isPhoneWeb() && !isDismissed());
    update();
    if (!window.matchMedia) return;
    const coarse = window.matchMedia("(pointer: coarse)");
    const narrow = window.matchMedia("(max-width: 640px)");
    coarse.addEventListener("change", update);
    narrow.addEventListener("change", update);
    return () => {
      coarse.removeEventListener("change", update);
      narrow.removeEventListener("change", update);
    };
  }, []);

  if (!visible) return null;

  const deepLink = buildDeepLink(location.pathname, location.search);

  const dismiss = () => {
    rememberDismissed();
    setVisible(false);
  };

  return (
    <div className="border-b border-outline-variant/40 bg-surface-container-lowest sm:hidden">
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-2.5">
        <span
          aria-hidden="true"
          className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary-fixed"
        >
          <BrandMark className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-label-md text-on-surface">
            Open in the Tailspend app
          </p>
          <p className="truncate text-body-sm text-on-surface-variant">
            A faster experience on your phone.
          </p>
        </div>
        <a
          href={deepLink}
          className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md bg-primary px-3 text-label-md text-on-primary transition-colors hover:bg-primary-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          <Smartphone className="size-4" aria-hidden="true" />
          Open
        </a>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="grid size-9 shrink-0 place-items-center rounded-full text-on-surface-variant transition-colors hover:bg-surface-container hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
